import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, Clock, Shield } from "lucide-react";
import { useVault } from "@/context/VaultContext";
import { listSessions, contactsMap, nameOf, timerLabel } from "@/lib/selectors";
import { Avatar } from "@/components/Loader";
import { OrbitalLock } from "@/components/aph/OrbitalLock";
import { LockDialog } from "@/components/aph/LockDialog";
import { MessageBubble } from "@/components/aph/MessageBubble";
import { Composer } from "@/components/aph/Composer";
import { TimerSelect } from "@/components/aph/TimerSelect";

const BANNER = {
  connected: ["text-stellar/70", "Orbital locking in place. Connection encrypted."],
  pending: ["text-ash/50", "Handshake pending — messages wait sealed on the relay until your contact comes online."],
  compromised: ["text-flare", "Signature mismatch detected. Verify the safety number before continuing."],
};

export function ChatWindow() {
  const { q, version, profile, activeSid, setActiveSid, markRead } = useVault();
  const [lockOpen, setLockOpen] = useState(false);
  const endRef = useRef(null);
  /* eslint-disable react-hooks/exhaustive-deps */
  const session = useMemo(() => listSessions(q, profile).find((s) => s.session_id === activeSid), [version, activeSid]);
  const contacts = useMemo(() => contactsMap(q), [version]);
  const messages = useMemo(() => q("SELECT * FROM messages WHERE session_id=? ORDER BY timestamp ASC", [activeSid]).map((m) => ({ ...m, content: JSON.parse(m.content || "{}"), delivered_to: JSON.parse(m.delivered_to), read_by: JSON.parse(m.read_by) })), [version, activeSid]);
  useEffect(() => { if (document.visibilityState === "visible") markRead(activeSid); }, [version, activeSid]);
  /* eslint-enable react-hooks/exhaustive-deps */
  useEffect(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), [messages.length]);

  if (!session) return null;
  const [tone, text] = BANNER[session.lock];

  return (
    <section className="flex-1 flex flex-col min-w-0 bg-void" data-testid="chat-window">
      <header className="flex items-center justify-between gap-3 px-4 py-3 bg-nebula border-b border-glass">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => setActiveSid(null)} data-testid="chat-back-btn" className="md:hidden p-1.5 text-ash/60 hover:text-stellar"><ArrowLeft size={20} strokeWidth={1.5} /></button>
          <Avatar name={session.name} group={session.kind === "group"} />
          <div className="min-w-0">
            <h1 className="font-branding text-lg font-semibold uppercase tracking-wider truncate" data-testid="chat-title">{session.name}</h1>
            <p className="text-xs text-flare flex items-center gap-1 whitespace-nowrap" data-testid="chat-timer-label">
              <Clock size={12} strokeWidth={1.5} /> {session.self_destruct_timer ? <span><span className="hidden sm:inline">Self-destruct: </span>{timerLabel(session.self_destruct_timer)}</span> : <span className="text-ash/45">Burn off</span>}
              {session.kind === "group" && <span className="text-ash/45">· {session.members.length} members</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <TimerSelect sid={session.session_id} value={session.self_destruct_timer} />
          <OrbitalLock status={session.lock} onClick={() => setLockOpen(true)} />
        </div>
      </header>
      <main className="flex-1 overflow-y-auto px-4 sm:px-8 py-5 flex flex-col gap-3 starfield" data-testid="message-list">
        <div className="flex justify-center mb-4">
          <span className={`text-xs bg-nebula px-3 py-1.5 rounded-secure flex items-center gap-2 border border-glass text-center ${tone}`} data-testid="orbital-banner"><Shield size={14} strokeWidth={1.5} /> {text}</span>
        </div>
        {messages.map((m) => (
          <MessageBubble key={m.message_id} m={m} mine={m.sender_code === profile.code} senderName={session.kind === "group" || m.content.sys ? nameOf(contacts[m.sender_code]) : null} peers={session.peers.length} />
        ))}
        <div ref={endRef} />
      </main>
      <Composer sid={session.session_id} />
      <LockDialog open={lockOpen} onOpenChange={setLockOpen} session={session} contacts={contacts} />
    </section>
  );
}
